import {
    Avatar,
    Box,
    Button,
    IconButton,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import MuiChatHistoryBar from "../../components/ChatHistory/MuiChatHistoryBar";
import { useAppStateContext } from "../../state/AppContextProvider";
import { MuiSidebar } from "./MuiSidebar";
import PromptManager from "../../components/PromptManagement/PromptManager";
import RegularIcon7 from "/src/assets/icons-regular-7.svg";
import RegularIcon8 from "/src/assets/icons-regular-8.svg";
import HeaderIcon from "/src/assets/FLWM-FULL LOGO DARK.svg";
import HeaderIconDark from "/src/assets/FLWM-FULL LOGO LIGHT.svg";
import { UserInfo } from "../../common/models";
import { useMsal } from "@azure/msal-react";
import { Menu, MenuItem } from "@mui/material";
import FAQManager from "../../components/FAQManagement/FAQManager";
import GlossaryManager from "../../components/GlossaryManagement/GlossaryManager";
import ApprovalManagement from "../../components/common/Approvals/ApprovalManagement";
import Playground from "../../components/Playground/Playground";
import { HttpServiceContext } from "../../state/HttpProvider";
import { AccessContext } from "../../state/AccessProvider";

export const MuiAdmin = (): JSX.Element => {
    const appStateContext = useAppStateContext();
    const [activeTab, setActiveTab] = useState(0); // State to track the active tab
    const [unassignedUsers, setUnassignedUsers] = useState<UserInfo[]>([]);
    const [approvedUsers, setApprovedUsers] = useState<UserInfo[]>([]);

    const {httpService: apiService } = useContext(HttpServiceContext)
    const { accounts, instance } = useMsal();
    const profilePhoto = appStateContext.profilePhoto;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isPhone] = useState(navigator.userAgent.includes('Mobile'));
    const { isAdmin, isDeveloper, isAdvancedUser, hasPlaygroundAccess } = appStateContext;
    const {handleLogout} = useContext(AccessContext)

    const fetchUnassignedUsers = async () => {
        try {
            const users = await apiService.getUnassignedUsers();
            setUnassignedUsers(users);
        } catch (error) {
            console.error("Error fetching unassigned users:", error);
        }
    };

    const fetchApprovedUsers = async () => {
        try {
            const users = await apiService.getApprovedUsers();
            setApprovedUsers(users);
        } catch (error) {
            console.error("Error fetching approved users:", error);
        }
    };

    const approveUser = async (userId: string) => {
        try {
            await apiService.approveUser(userId);
            fetchUnassignedUsers();
            fetchApprovedUsers();
        } catch (error) {
            console.error("Error approving user:", error);
        }
    };

    const removeUser = async (userId: string) => {
        try {
            await apiService.removeUser(userId);
            fetchApprovedUsers();
        } catch (error) {
            console.error("Error removing user:", error);
        }
    };

    useEffect(() => {
        if (activeTab === 0) {
            fetchUnassignedUsers();
            fetchApprovedUsers();
        }
    }, [activeTab]);

    // Reset active tab on mount and when role/permission flags change
    useEffect(() => {
        setActiveTab(0);
    }, [isAdmin, isDeveloper, hasPlaygroundAccess]);
    useEffect(() => {
        appStateContext?.getChatHistory();
    }, []);

    const handleHistoryClick = () => {
        appStateContext?.dispatch({ type: "TOGGLE_CHAT_HISTORY" });
    };

    const newChat = async () => {
        appStateContext.dispatch({
            type: "SET_ACTIVE_DOCUMENT",
            payload: null,
        });


        //TODO: review if this is still needed

        // // Set the conversation flow to DECEPTION before inserting the prompt
        // appStateContext.dispatch({ type: 'SET_CONVERSATION_FLOW', payload: 'NEGOTIATION' });
        
        appStateContext.clearCurrentChat();
        appStateContext?.getChatHistory();
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    useEffect(() => {
        if (activeTab === 0) {
            fetchUnassignedUsers();
            fetchApprovedUsers();
        }
    }, [activeTab]);


    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box
                sx={{
                    position: "relative",
                    height: "100vh",
                    bgcolor: "background.default",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Header - Full Width */}
                <Box
                    component="header"
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: "var(--header-height)",
                        bgcolor: "background.paper",
                        borderBottom: 1,
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 4,
                        py: 2,
                        zIndex: 1100,
                    }}
                >
                    <Box
                        component="img"
                        src={appStateContext.state.themeMode === 'light' ? HeaderIcon : HeaderIconDark}
                        alt="NeuAI icon"
                        sx={{
                            width: isPhone ? 90 : '15%',
                            maxWidth: isPhone ? '90px' : '245px',
                            pl: isPhone ? 0 : '50px',
                        }}
                    />

                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton
                            sx={{
                                bgcolor: "#e50c20",
                                borderRadius: 3,
                                width: { xs: "34px", sm: "40px" },
                                height: { xs: "34px", sm: "40px" },
                                "&:hover": { bgcolor: "#b8091a" },
                            }}
                            onClick={newChat}
                        >
                            <Box
                                component="img"
                                src={RegularIcon7}
                                alt="Action"
                                width={isPhone ? 18 : 24}
                                height={isPhone ? 18 : 24}
                            />
                        </IconButton>

                        <IconButton onClick={handleHistoryClick}
                            sx={{
                                width: isPhone ? 32 : 40,
                                height: isPhone ? 32 : 40,
                            }}
                        >
                            <Box
                                component="img"
                                src={RegularIcon8}
                                alt="Menu"
                                width={isPhone ? 18 : 24}
                                height={isPhone ? 18 : 24}
                            />
                        </IconButton>
                    </Stack>
                </Box>

                {/* Middle Section: Sidebar + Main Content */}
                <Box sx={{ 
                    display: "flex", 
                    flex: 1, 
                    position: "relative",
                    overflow: "hidden"
                }}>
                    {/* Sidebar */}
                    <MuiSidebar
                        profilePhoto={profilePhoto}
                        handleMenu={handleMenu}
                        anchorEl={anchorEl}
                        handleClose={handleClose}
                        onSidebarInsert={() => {}}
                    />

                    {/* Main Content Area */}
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        {/* Tabs */}
                        <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        textColor="primary"
                        indicatorColor="primary"
                        centered
                        sx={{
                            flexShrink: 0,
                            borderBottom: 1,
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            "& .MuiTab-root": {
                                color: "custom.lockedPrimary",
                                fontWeight: 500,
                                borderRadius: "8px 8px 0 0",
                                minHeight: 48,
                                transition: "background 0.2s, color 0.2s",
                            },
                            "& .Mui-selected": {
                                color: "#fff",
                                backgroundColor: "custom.brandPrimary",
                                border: "2px solid",
                                borderColor: "custom.brandPrimary",
                                borderBottom: "none",
                                zIndex: 1,
                            },
                            "& .MuiTabs-indicator": {
                                bgcolor: "transparent",
                            },
                        }}
                    >
                        {/* Authorize tab only for admin */}
                        {isAdmin && <Tab label="Authorize" />}
                        {/* FAQ and Glossary visible to admin only (available on mobile) */}
                        {isAdmin && <Tab label="FAQ" />}
                        {isAdmin && <Tab label="Glossary" />}
                        {/* Prompt Development: desktop only, visible to developers */}
                        {!isPhone && isDeveloper && <Tab label="Prompt Development" />}
                        {/* Playground: desktop only, visible to users granted playground access */}
                        {!isPhone && hasPlaygroundAccess && <Tab label="Playground" />}
                    </Tabs>

                    {/* Tab Content - using dynamic tab indexing based on visible tabs */}
                    {(() => {
                        let tabIndex = 0;
                        
                        // Authorize tab (only for admin)
                        if (isAdmin && activeTab === tabIndex++) {
                            return <ApprovalManagement apiService={apiService} />;
                        }
                        
                        // FAQ tab (for admin only)
                        if (isAdmin && activeTab === tabIndex++) {
                            return (
                                <Box sx={{ p: 3 }}>
                                    <Typography variant="h6">FAQ</Typography>
                                    <FAQManager />
                                </Box>
                            );
                        }
                        
                        // Glossary tab (for admin only)
                        if (isAdmin && activeTab === tabIndex++) {
                            return (
                                <Box sx={{ p: 3 }}>
                                    <Typography variant="h6">Glossary</Typography>
                                    <GlossaryManager />
                                </Box>
                            );
                        }
                        
                        // Prompt Development tab (for developers only, not on phone)
                        if (!isPhone && isDeveloper && activeTab === tabIndex++) {
                            return (
                                <Stack
                                    sx={{
                                        position: "relative",
                                        right: 0,
                                        bottom: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        maxHeight: "calc(100vh - var(--header-height))",
                                        margin: "auto",
                                        overflow: "auto",
                                        height: "100%",
                                        width: "100%",
                                    }}
                                >
                                    <PromptManager />
                                </Stack>
                            );
                        }
                        
                        // Playground tab (for admin, developer, and playground roles, not on phone)
                        if (!isPhone && hasPlaygroundAccess && activeTab === tabIndex++) {
                            return (
                                <Box
                                    sx={{
                                        position: "relative",
                                        display: "flex",
                                        flexDirection: "column",
                                        height: "100%",
                                        width: "100%",
                                        overflow: "hidden",
                                        flex: 1,
                                    }}
                                >
                                    <Playground />
                                </Box>
                            );
                        }
                        
                        return null;
                    })()}
                    </Box>
                </Box>
            </Box>
            <MuiChatHistoryBar />
        </>
    );
};